import { useCallback, useState } from 'react'

import { API } from '@/api'
import { Button, Input, message, Trans } from '@/components'
import { PrivilegeType } from '@/consts/team'
import { Team } from '@/models/team'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'

import { ForgotTeamPasswordModal } from './ForgotTeamPasswordModal'

export function JoinTeam() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const teamStore = useTeamStore()
  const userStore = useUserStore()
  const [loading, setLoading] = useState(false)
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)

  const onLogin = useCallback(async () => {
    if (loading) {
      return
    }

    setLoading(true)
    try {
      // Bind platform account to team account (uses platform token)
      const res = await API.bindTeamMember({ name: email, password })

      if (res.code === 0) {
        // Parse target team resID from email
        const emailMatch = email.match(/@(team-[a-z0-9]+)\./)
        if (!emailMatch) {
          message.error('Invalid team account format')
          return
        }
        const targetResID = emailMatch[1]

        // Get team list and find the matching team (uses platform token)
        const teamsRes = await API.getTeamList({ page: 1, per_page: 100 })
        const teams = teamsRes.teams.map((item) => new Team(item))
        const team = teams.find((t) => t.resID === targetResID)

        if (!team) {
          message.error('Team not found or not accessible')
          return
        }

        // Get team token using platform token
        const tokenRes = await API.getTeamToken({
          privilege_type: PrivilegeType.TeamToken,
          source_id: team.id,
        })

        // Set team context (15-minute token for switched teams)
        const { TEAM_TOKEN_EXPIRY_MS } = await import('@/utils/auth')
        teamStore.setTeamContext({
          token: `Bearer ${tokenRes.token}`,
          teamId: team.id,
          teamResID: team.resID,
          expiryTime: Date.now() + TEAM_TOKEN_EXPIRY_MS,
          userName: team.user.name,
        })

        // Update user store in background
        userStore.getUserInfo().catch((err) => {
          console.error('Failed to update user info:', err)
        })

        // Navigate to team profile (full page reload to ensure clean state)
        window.location.href = '/team/profile'
      }
    } catch (error) {
      // http interceptor already shows error messages, just log
      console.error('Join team failed:', error)
    } finally {
      setLoading(false)
    }
  }, [email, password, loading, teamStore, userStore])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[image:var(--image-app-background)] bg-cover bg-center bg-no-repeat">
      <div className="flex flex-col items-center gap-y-8 md:px-6">
        <div className="card bg-panel-white w-[480px] md:w-full flex flex-col items-center gap-4">
          <div className="w-full py-2 flex flex-col items-center gap-4">
            <h2 className="text-primary h5 text-center">
              <Trans i18nKey="joinTeam.title">Join Team</Trans>
            </h2>

            <div className="w-full text-primary">
              <div className="mb-2 t-sb">
                <Trans i18nKey="joinTeam.labelAccount">Account</Trans>
              </div>
              <Input
                placeholder="Account"
                value={email}
                disabled={loading}
                className="t-s-sb"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="w-full text-primary">
              <div className="mb-2 t-sb">
                <Trans i18nKey="joinTeam.labelPassword">Password</Trans>
              </div>
              <Input
                placeholder="Password"
                type="password"
                value={password}
                disabled={loading}
                className="t-s-sb"
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <Button type="secondary" block className="mt-2" onClick={onLogin} disabled={loading}>
              <Trans i18nKey="joinTeam.loginBtn">Login</Trans>
            </Button>

            <div className="flex mt-4 t-sb md:t-sm gap-2">
              <button
                type="button"
                className="text-btn-secondary font-bold cursor-pointer hover:underline"
                onClick={() => setShowForgotPasswordModal(true)}
              >
                <Trans i18nKey="joinTeam.forgotPassword">forgot password?</Trans>
              </button>
            </div>
          </div>
        </div>
        <div className="max-w-[480px] text-center t-sm text-secondary">
          <Trans i18nKey="joinTeam.agreement">
            By registering on our platform means that you have read and agree to our
            <a className="text-primary" href="https://glows.ai/eula">
              EULA
            </a>
            ,
            <a className="text-primary" href="https://glows.ai/tos">
              Terms of Service
            </a>
            , and
            <a className="text-primary" href="https://glows.ai/privacy">
              Privacy Policy
            </a>
            .
          </Trans>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotTeamPasswordModal
        open={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </div>
  )
}
